<template>
  <div>
    <dynamic-data-table
      :can-delete="true"
      :can-edit="true"
      :is-loading="tablePermissionsStatus === 'loading'"
      :table-headers="permissionHeaders"
      :table-records="tableSettingsPermissions"
      table-title="Permissions"
      @deleteItem="setupDelete"
      @editItem="setupEdit"
    >
      <template #item.role="{ value }">
        <td>{{ value?.name }}</td>
      </template>
      <template #item.create="{ value, permissionId }">
        <v-simple-checkbox :ripple="false" :value="value" disabled />
      </template>
      <template #item.update="{ value }">
        <v-simple-checkbox :ripple="false" :value="value" disabled />
      </template>
      <template #item.read="{ value }">
        <v-simple-checkbox :ripple="false" :value="value" disabled />
      </template>
      <template #item.delete="{ value }">
        <v-simple-checkbox :ripple="false" :value="value" disabled />
      </template>
      <template #item.configure="{ value }">
        <v-simple-checkbox :ripple="false" :value="value" disabled />
      </template>
      <template slot="table-actions">
        <v-dialog
          key="add-edit-permission"
          v-model="permissionDialog"
          max-width="500px"
          @click:outside="closePermissionDialogs"
          @keydown.esc="closePermissionDialogs"
        >
          <template #activator="{ on, attrs }">
            <v-tooltip left>
              <template #activator="{ on: onTooltip }">
                <v-icon class="ant-icon" dense v-on="{ ...on, ...onTooltip }">
                  mdi-plus
                </v-icon>
              </template>
              <span>New permission</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title class="justify-center text-uppercase headline">
              {{ permissionItem.id ? 'Edit' : 'Add' }} Permission
            </v-card-title>
            <v-divider />
            <div class="px-10 pb-5">
              <ant-input label="Team">
                <template #input-field>
                  <v-select
                    v-if="!permissionItem.id"
                    v-model="permissionItem.role"
                    :items="nonConfiguredRoles"
                    dense
                    filled
                    hide-details
                    item-text="name"
                    item-value="id"
                    placeholder="Team"
                    return-object
                    single-line
                  />
                  <v-text-field
                    v-else
                    :value="
                      tableSettingsTeams.find(
                        (team) => team.id === permissionItem.group
                      ).name
                    "
                    dense
                    disabled
                    filled
                    hide-details
                    single-line
                  />
                </template>
              </ant-input>
              <v-row>
                <v-col cols="12" md="4" sm="6">
                  <v-checkbox
                    v-model="permissionItem.create"
                    class="mx-2"
                    hide-details
                    label="Create"
                  />
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <v-checkbox
                    v-model="permissionItem.read"
                    class="mx-2"
                    hide-details
                    label="Read"
                  />
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <v-checkbox
                    v-model="permissionItem.update"
                    class="mx-2"
                    hide-details
                    label="Update"
                  />
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <v-checkbox
                    v-model="permissionItem.delete"
                    class="mx-2"
                    hide-details
                    label="Delete"
                  />
                </v-col>
                <v-col cols="12" md="4" sm="6">
                  <v-checkbox
                    v-model="permissionItem.configure"
                    class="mx-2"
                    hide-details
                    label="Configure"
                  />
                </v-col>
              </v-row>
            </div>

            <v-card-actions class="ant-dialog-actions-bg ant-border-top">
              <v-spacer />
              <v-btn color="error" small text @click="closePermissionDialogs">
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                elevation="0"
                small
                @click="savePermission"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </dynamic-data-table>
    <delete-dialog
      :dialog="permissionDeleteDialog"
      title="Are you sure you want to remove all permissions for this team"
      @closeDialog="closePermissionDialogs"
      @deleteAction="deletePermission"
    />
  </div>
</template>

<script>
import DynamicDataTable from '@/components/DynamicDataTable';
import { mapGetters } from 'vuex';
import DeleteDialog from '@/components/DeleteDialog';
import AntInput from '@/components/AntInput.vue';

export default {
  name: 'TablePermissions',
  components: { AntInput, DeleteDialog, DynamicDataTable },
  data: () => {
    return {
      permissionHeaders: [
        {
          text: 'team',
          value: 'role',
          hasSlot: true,
        },
        {
          text: 'create',
          value: 'create',
          hasSlot: true,
        },
        {
          text: 'read',
          value: 'read',
          hasSlot: true,
        },
        {
          text: 'update',
          value: 'update',
          hasSlot: true,
        },
        {
          text: 'delete',
          value: 'delete',
          hasSlot: true,
        },
        {
          text: 'configure',
          value: 'configure',
          hasSlot: true,
        },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false },
      ],
      permissionItem: {
        create: false,
        read: false,
        update: false,
        delete: false,
      },
      permissionDialog: false,
      permissionDeleteDialog: false,
    };
  },
  computed: {
    ...mapGetters([
      'selectedProjectTable',
      'tableSettingsPermissions',
      'tableSettingsTeams',
      'tablePermissionsStatus',
    ]),
    nonConfiguredRoles() {
      if (this.tableSettingsPermissions.length > 0) {
        return this.tableSettingsTeams.filter((team) => {
          return (
            this.tableSettingsPermissions.find(
              (permission) => permission.role.id !== team.id
            ) && !team.is_admin
          );
        });
      } else {
        return this.tableSettingsTeams;
      }
    },
  },
  methods: {
    closePermissionDialogs() {
      this.permissionItem = Object.assign(
        {},
        {
          create: false,
          read: false,
          update: false,
          delete: false,
        }
      );
      this.permissionDialog = false;
      this.permissionDeleteDialog = false;
    },
    setupEdit(permission) {
      this.permissionItem = Object.assign({}, permission);
      this.permissionDialog = true;
    },
    setupDelete(permission) {
      this.permissionItem = Object.assign({}, permission);
      this.permissionDeleteDialog = true;
    },
    deletePermission() {
      this.$store
        .dispatch('deletePermission', {
          permissionId: this.permissionItem.id,
          body: {
            table: {
              id: this.selectedProjectTable.id,
            },
            project: {
              id: this.selectedProjectTable.project,
            },
          },
        })
        .then(() => {
          this.closePermissionDialogs();
        });
    },
    savePermission() {
      let data = Object.assign({}, this.permissionItem);

      delete data.group;
      delete data.id;
      delete data.table;

      let body = {
        project: {
          id: this.selectedProjectTable.project,
        },
        table: {
          id: this.selectedProjectTable.id,
        },
        group: {
          id: this.permissionItem.group,
        },
      };

      Object.keys(data).forEach((key, index) => {
        body[key] = data[key];
      });

      if (this.permissionItem.id) {
        // update permission
        this.$store
          .dispatch('updatePermission', {
            permissionId: this.permissionItem.id,
            body,
          })
          .then(() => {
            this.closePermissionDialogs();
          });
      } else {
        // create permission
        this.$store.dispatch('createPermission', { body }).then(() => {
          this.closePermissionDialogs();
        });
      }
    },
  },
};
</script>

<style scoped></style>
